// ====== components/products/ProductLabelGenerator.js ======
import React, { useState, useRef, useEffect } from 'react';
import { 
  Modal, Card, Row, Col, Form, Button, Alert, 
  Badge, InputGroup, Table, Spinner
} from 'react-bootstrap';
import { 
  Printer, Package, Settings, Eye, Download,
  Plus, Minus, RotateCw, Check, X, Tag, Search
} from 'lucide-react';
import { labelService, labelUtils } from '../../services/labelService';
import productService from '../../services/productService';
import BarcodeGenerator from '../common/BarcodeGenerator';
import { useAuth } from '../../contexts/AuthContext';

const ProductLabelGenerator = ({ 
  show, 
  onHide, 
  preSelectedProducts = [],
  onProductScan 
}) => {
  // États
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copies, setCopies] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Configuration étiquettes
  const [labelConfig, setLabelConfig] = useState({
    format: 'medium',
    showPrice: true,
    showSku: true
  });

  // États pour l'aperçu
  const [showPreview, setShowPreview] = useState(false);
  const [generatedLabels, setGeneratedLabels] = useState(null);
  const [error, setError] = useState('');

  const { user } = useAuth();
  const printRef = useRef();

  // Charger les produits au montage
  useEffect(() => {
    if (show) {
      loadProducts();
      initializeFromPreSelected();
    }
  }, [show, preSelectedProducts]);

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await productService.getProducts({
        page: 0,
        size: 100,
        sortBy: 'name',
        sortDirection: 'ASC'
      });

      if (response.success) {
        const productsData = response.data.content || response.data || [];
        setProducts(productsData);
        
        // Initialiser les copies
        const initialCopies = {};
        productsData.forEach(product => {
          initialCopies[product.id] = 1;
        });
        setCopies(initialCopies);
        
      } else {
        setError(response.message || 'Erreur lors du chargement des produits');
      }
    } catch (error) {
      console.error('Erreur chargement produits:', error);
      setError('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const initializeFromPreSelected = () => {
    if (preSelectedProducts && preSelectedProducts.length > 0) {
      setSelectedProducts([...preSelectedProducts]);
    }
  };

  // Filtrer les produits selon la recherche
  const filteredProducts = products.filter(product => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      product.name?.toLowerCase().includes(query) ||
      product.sku?.toLowerCase().includes(query) ||
      product.barcode?.toLowerCase().includes(query) ||
      product.categoryName?.toLowerCase().includes(query)
    );
  });

  const handleProductSelect = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.find(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts([...filteredProducts]);
    }
  };

  const updateCopies = (productId, newCopies) => {
    setCopies(prev => ({
      ...prev,
      [productId]: Math.max(1, Math.min(10, newCopies))
    }));
  };

  const generateLabels = async () => {
    if (selectedProducts.length === 0) {
      setError('Aucun produit sélectionné');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      // Préparer la requête
      const request = {
        productIds: selectedProducts.map(p => p.id),
        format: labelConfig.format,
        showPrice: labelConfig.showPrice,
        showSku: labelConfig.showSku,
        copies: selectedProducts.reduce((acc, product) => {
          acc[product.id] = copies[product.id] || 1;
          return acc;
        }, {})
      };

      const response = await labelService.generateProductLabels(request);

      if (response.success) {
        setGeneratedLabels(response.data);
        setShowPreview(true);
      } else {
        setError(response.message || 'Erreur lors de la génération des étiquettes');
      }
    } catch (error) {
      console.error('Erreur génération:', error);
      setError('Erreur lors de la génération des étiquettes');
    } finally {
      setGenerating(false);
    }
  };

  const printLabels = () => {
    const printContent = printRef.current?.innerHTML;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Étiquettes Produits - ${user?.boutiqueName || 'SmartBoutik'}</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              font-family: Arial, sans-serif; 
              background: white;
            }
            .product-label {
              border: 1px dashed #ccc;
              padding: 8px;
              margin: 4px;
              background: white;
              display: inline-block;
              vertical-align: top;
              page-break-inside: avoid;
              position: relative;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .label-small { width: 160px; height: 100px; font-size: 8px; }
            .label-medium { width: 200px; height: 120px; font-size: 9px; }
            .label-large { width: 240px; height: 140px; font-size: 10px; }
            @media print {
              body { margin: 0; padding: 10px; }
              .product-label { page-break-inside: avoid; margin: 2px; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  const downloadPDF = () => {
    // Fonctionnalité à implémenter avec jsPDF
    alert('Fonctionnalité PDF en cours de développement.\nUtilisez l\'impression en attendant.');
  };

  const totalLabels = labelUtils.calculateTotalLabels(
    selectedProducts.reduce((acc, product) => {
      acc[product.id] = copies[product.id] || 1;
      return acc;
    }, {})
  );

  const formatPrice = (price) => labelUtils.formatPrice(price);

  return (
    <>
      <Modal 
        show={show} 
        onHide={onHide}
        size="xl"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Tag className="me-2" />
            Générateur d'étiquettes avec codes-barres
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <Card.Header className="bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Sélection des produits</h6>
                    <Badge bg="primary">
                      {selectedProducts.length} sélectionné(s)
                    </Badge>
                  </div>
                </Card.Header>

                <Card.Body>
                  {/* Recherche */}
                  <Row className="mb-3">
                    <Col md={8}>
                      <InputGroup>
                        <InputGroup.Text>
                          <Search size={16} />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Rechercher par nom, SKU, code-barres..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={4}>
                      <Button 
                        variant="outline-primary" 
                        onClick={handleSelectAll}
                        className="w-100"
                      >
                        {selectedProducts.length === filteredProducts.length ? 
                          'Tout désélectionner' : 'Tout sélectionner'}
                      </Button>
                    </Col>
                  </Row>

                  {/* Tableau des produits */}
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="primary" />
                      <div className="mt-2">Chargement des produits...</div>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <Table responsive hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th width="50">
                              <Form.Check
                                checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                                indeterminate={selectedProducts.length > 0 && selectedProducts.length < filteredProducts.length}
                                onChange={handleSelectAll}
                              />
                            </th>
                            <th>Produit</th>
                            <th>Code-barres</th>
                            <th>Prix</th>
                            <th>Stock</th>
                            <th width="120">Exemplaires</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredProducts.map(product => {
                            const isSelected = selectedProducts.find(p => p.id === product.id);
                            return (
                              <tr 
                                key={product.id}
                                className={isSelected ? 'table-primary' : ''}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleProductSelect(product)}
                              >
                                <td onClick={(e) => e.stopPropagation()}>
                                  <Form.Check
                                    checked={!!isSelected}
                                    onChange={() => handleProductSelect(product)}
                                  />
                                </td>
                                <td>
                                  <div>
                                    <strong>{product.name}</strong>
                                    <div className="small text-muted">
                                      {product.sku && `SKU: ${product.sku}`}
                                      {product.sku && product.categoryName && ' • '}
                                      {product.categoryName}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  {product.barcode ? (
                                    <code style={{ fontSize: '0.8em' }}>{product.barcode}</code>
                                  ) : (
                                    <small className="text-muted">Aucun</small>
                                  )}
                                </td>
                                <td className="text-primary fw-bold">
                                  {formatPrice(product.price)}
                                </td>
                                <td>
                                  <Badge bg={product.currentStock <= 5 ? 'warning' : 'success'}>
                                    {product.currentStock || 0}
                                  </Badge>
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  {isSelected && (
                                    <InputGroup size="sm">
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() => updateCopies(product.id, (copies[product.id] || 1) - 1)}
                                        disabled={copies[product.id] <= 1}
                                      >
                                        <Minus size={12} />
                                      </Button>
                                      <Form.Control
                                        type="number"
                                        onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                                        value={copies[product.id] || 1}
                                        onChange={(e) => updateCopies(product.id, parseInt(e.target.value) || 1)}
                                        min="1"
                                        max="10"
                                        className="text-center"
                                        style={{ maxWidth: '60px' }}
                                      />
                                      <Button
                                        variant="outline-secondary"
                                        onClick={() => updateCopies(product.id, (copies[product.id] || 1) + 1)}
                                        disabled={copies[product.id] >= 10}
                                      >
                                        <Plus size={12} />
                                      </Button>
                                    </InputGroup>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>

                      {filteredProducts.length === 0 && (
                        <div className="text-center py-4 text-muted">
                          <Package size={48} className="mb-3" />
                          <div>
                            {searchQuery ? 
                              'Aucun produit ne correspond à votre recherche' : 
                              'Aucun produit disponible'}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Configuration des étiquettes */}
              {selectedProducts.length > 0 && (
                <Card className="mt-3">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">
                      <Settings className="me-2" size={16} />
                      Configuration des étiquettes
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Format</Form.Label>
                          <Form.Select 
                            value={labelConfig.format} 
                            onChange={(e) => setLabelConfig(prev => ({ ...prev, format: e.target.value }))}
                          >
                            <option value="small">Petit (160×100px)</option>
                            <option value="medium">Moyen (200×120px)</option>
                            <option value="large">Grand (240×140px)</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Options d'affichage</Form.Label>
                          <div>
                            <Form.Check
                              type="checkbox"
                              label="Afficher le prix"
                              checked={labelConfig.showPrice}
                              onChange={(e) => setLabelConfig(prev => ({ ...prev, showPrice: e.target.checked }))}
                            />
                            <Form.Check
                              type="checkbox"
                              label="Afficher le SKU"
                              checked={labelConfig.showSku}
                              onChange={(e) => setLabelConfig(prev => ({ ...prev, showSku: e.target.checked }))}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <div className="d-flex justify-content-end align-items-end h-100">
                          <div className="text-end">
                            <div className="mb-2">
                              <strong>Total: {totalLabels} étiquette(s)</strong>
                            </div>
                            <Button
                              variant="primary"
                              onClick={generateLabels}
                              disabled={generating || selectedProducts.length === 0}
                            >
                              {generating ? (
                                <>
                                  <RotateCw className="me-2 spin" size={16} />
                                  Génération...
                                </>
                              ) : (
                                <>
                                  <Eye className="me-2" size={16} />
                                  Aperçu et impression
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Fermer
          </Button>
          {selectedProducts.length > 0 && (
            <Button 
              variant="primary" 
              onClick={generateLabels}
              disabled={generating}
            >
              <Printer className="me-2" size={16} />
              Générer les étiquettes
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal d'aperçu des étiquettes */}
      <Modal 
        show={showPreview} 
        onHide={() => setShowPreview(false)}
        size="xl"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Printer className="me-2" />
            Aperçu des étiquettes ({generatedLabels?.totalLabels || 0} étiquette(s))
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div ref={printRef} className="text-center">
            {generatedLabels?.products?.map(product => (
              Array.from({ length: product.copies || 1 }).map((_, copyIndex) => (
                <ProductLabelComponent
                  key={`${product.productId}-${copyIndex}`}
                  product={product}
                  format={generatedLabels.format}
                  showPrice={generatedLabels.showPrice}
                  showSku={generatedLabels.showSku}
                  boutiqueName={generatedLabels.boutiqueName}
                />
              ))
            ))}
          </div>
        </Modal.Body>
        
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              <small className="text-muted">
                Format: {generatedLabels?.format} • 
                Prix: {generatedLabels?.showPrice ? 'Oui' : 'Non'} • 
                SKU: {generatedLabels?.showSku ? 'Oui' : 'Non'}
              </small>
            </div>
            <div>
              <Button variant="secondary" onClick={() => setShowPreview(false)} className="me-2">
                Fermer
              </Button>
              <Button variant="outline-primary" onClick={downloadPDF} className="me-2">
                <Download className="me-2" size={16} />
                Télécharger PDF
              </Button>
              <Button variant="primary" onClick={printLabels}>
                <Printer className="me-2" size={16} />
                Imprimer
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .sticky-top {
          position: sticky;
          top: 0;
          z-index: 10;
        }
      `}</style>
    </>
  );
};

// Composant pour afficher une étiquette individuelle
const ProductLabelComponent = ({ 
  product, 
  format, 
  showPrice, 
  showSku, 
  boutiqueName 
}) => {
  const labelStyles = {
    small: { width: '160px', height: '100px', fontSize: '8px' },
    medium: { width: '200px', height: '120px', fontSize: '9px' },
    large: { width: '240px', height: '140px', fontSize: '10px' }
  };

  const style = labelStyles[format] || labelStyles.medium;

  return (
    <div 
      className="product-label"
      style={{
        width: style.width,
        height: style.height,
        border: '1px dashed #ccc',
        padding: '8px',
        margin: '4px',
        backgroundColor: '#fff',
        fontSize: style.fontSize,
        display: 'inline-block',
        verticalAlign: 'top',
        position: 'relative',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      {/* Nom du produit */}
      <div style={{ 
        fontWeight: 'bold', 
        lineHeight: '1.2', 
        marginBottom: '4px',
        wordWrap: 'break-word'
      }}>
        {labelUtils.truncateProductName(product.name, 25)}
      </div>
      
      {/* Code-barres */}
      {product.barcode && (
        <div style={{ marginBottom: '4px', textAlign: 'center' }}>
          <BarcodeGenerator 
            value={product.barcode}
            width={parseInt(style.width) - 16}
            height={30}
          />
        </div>
      )}
      
      {/* Prix */}
      {showPrice && (
        <div style={{ 
          fontWeight: 'bold', 
          color: '#0d6efd', 
          marginBottom: '2px' 
        }}>
          {labelUtils.formatPrice(product.price)}
        </div>
      )}
      
      {/* SKU */}
      {showSku && product.sku && (
        <div style={{ 
          color: '#666', 
          fontSize: '7px' 
        }}>
          SKU: {product.sku}
        </div>
      )}
      
      {/* Logo boutique */}
      <div style={{ 
        position: 'absolute', 
        top: '4px', 
        right: '4px', 
        fontSize: '6px', 
        color: '#999' 
      }}>
        {boutiqueName || 'SmartBoutik'}
      </div>
    </div>
  );
};

export default ProductLabelGenerator;

